'use client';

import { useEffect } from 'react';
import { HttpTypes } from '@medusajs/types';
import {
  usePixelTracking,
  useInitiateCheckout,
  useProductView,
  usePurchase,
} from '@/lib/pixel/hooks';

export function PageViewTracker({ storeId }: { storeId?: string }) {
  usePixelTracking({ storeId });
  return null;
}

export function ProductViewTracker({
  product,
  storeId,
}: {
  product: HttpTypes.StoreProduct;
  storeId?: string;
}) {
  const { trackProductView } = useProductView(storeId);

  useEffect(() => {
    if (!product?.id) {
      return;
    }

    const lowestPrice = product.variants?.reduce((min, variant) => {
      const amount = variant.calculated_price?.calculated_amount;
      return typeof amount === 'number' && amount < min ? amount : min;
    }, Number.POSITIVE_INFINITY);

    trackProductView({
      id: product.id,
      title: product.title || '',
      price: Number.isFinite(lowestPrice) ? lowestPrice : undefined,
      currency: product.variants?.[0]?.calculated_price?.currency_code?.toUpperCase(),
    });
  }, [product, trackProductView]);

  return null;
}

export function CheckoutStartedTracker({
  cart,
  storeId,
}: {
  cart: HttpTypes.StoreCart | null;
  storeId?: string;
}) {
  const { trackInitiateCheckout } = useInitiateCheckout(storeId);

  useEffect(() => {
    if (!cart?.items?.length) {
      return;
    }

    trackInitiateCheckout({
      id: cart.id,
      items: cart.items,
      total: cart.total || 0,
      currency_code: cart.currency_code || 'USD',
    });
  }, [cart, trackInitiateCheckout]);

  return null;
}

export function PurchaseTracker({
  order,
  storeId,
}: {
  order: HttpTypes.StoreOrder;
  storeId?: string;
}) {
  const { trackPurchase } = usePurchase(storeId);

  useEffect(() => {
    if (!order?.id) {
      return;
    }

    trackPurchase({
      id: order.id,
      items: order.items || [],
      total: order.total || 0,
      currency_code: order.currency_code || 'USD',
    });
  }, [order, trackPurchase]);

  return null;
}